<template>
  <modal
    :show.sync="modalOpen"
  >
    <h4
      slot="header"
      class="modal-title"
      id="modal-title-default"
    >
      Upload Match Schedule
    </h4>
    <div class="row">
      <div class="col-12">
        <div class="form-group has-label">
          <label class="form-control-label">
            Schedule CSV File
          </label>
          <input
            type="file"
            class="form-control form-control-alternative"
            ref="file"
            @change="handleFileUpload"
          />
        </div>
      </div>
    </div>
    <div class="row" v-if="allErrors.length > 0">
      <div class="col">
        <h4>Errors</h4>
        <div class="error-box">
          <div
            :class="['error', {'warn': error.warnOnly}]"
            v-for="(error, index) in allErrors"
            :key="index"
          >
            <b v-if="error.row">
              Row {{error.row}}
            </b>
            {{error.error}}
          </div>
        </div>
      </div>
    </div>
    <template slot="footer">
      <base-button
        type="link"
        @click="onClose"
      >
        Cancel
      </base-button>
      <base-button
        type="success"
        class="ml-auto"
        :disabled="!(!loading && canUpload)"
        @click="uploadSchedule"
      >
        Upload
      </base-button>
    </template>
  </modal>
</template>
<script>
import PapaParse from 'papaparse';
import Moment from 'moment';
import ErrorHandler from '@/helpers/AuthError';

export default {
  name: 'BulkScheduleUploadModal',
  props: {
    open: Boolean,
    league: Object,
  },
  watch: {
    csvFile() {
      console.log(this.csvFile);
    },
    league() {
      console.log(this.league);
      this.getAllLeagueMatches();
    },
  },
  data() {
    return {
      csvFile: null,
      loading: false,
      csvData: [],
      csvErrors: [],
      csvFields: [],
      simulators: [],
      matches: [],
    };
  },
  mounted() {
    this.getAllSimulators();
    this.getAllLeagueMatches();
  },
  computed: {
    modalOpen: {
      get() {
        return this.open;
      },
      set() {
        this.onClose();
      },
    },
    areFieldsValid() {
      const errors = [];
      if (this.csvData.length > 0) {
        if (this.csvFields.length <= 0) {
          errors.push({ error: 'No columns were found.' });
        }
        if (!this.csvFields.includes('Week')) {
          errors.push({ error: '\'Week\' column is missing.' });
        }
        if (!this.csvFields.includes('Team 1')) {
          errors.push({ error: '\'Team 1\' column is missing.' });
        }
        if (!this.csvFields.includes('Team 2')) {
          errors.push({ error: '\'Team 2\' column is missing.' });
        }
        if (!this.csvFields.includes('Simulator')) {
          errors.push({ error: '\'Simulator\' column is missing.' });
        }
        if (!this.csvFields.includes('TIME')) {
          errors.push({ error: '\'TIME\' column is missing.' });
        }
      }
      return errors;
    },
    // All matches have two teams
    matchesHaveTwoTeams() {
      const errors = [];
      this.csvData.forEach((row, index) => {
        if (!row['Team 1'] || row['Team 1'].length <= 0) {
          errors.push({
            row: index,
            error: 'Missing Team 1',
          });
        }
        if (this.league.type === 'team_live' && (!row['Team 2'] || row['Team 2'].length <= 0)) {
          errors.push({
            row: index,
            error: 'Missing Team 2',
          });
        }
      });

      return errors;
    },
    // All weeks exist
    allWeeksExist() {
      const allWeeks = [];
      const errors = [];

      const leagueWeeks = this.league ? this.league.weeks : [];

      leagueWeeks.forEach((week) => {
        allWeeks.push(week.WeekNumber);
      });

      this.csvData.forEach((row, index) => {
        if (!allWeeks.includes(row.Week)) {
          errors.push({
            row: index,
            error: `Week ${row.Week} does not exist in league`,
          });
        }
      });

      return errors;
    },
    // All simulators exist
    allSimulatorsExist() {
      const errors = [];
      const allSims = [];

      this.simulators.forEach((sim) => {
        allSims.push((String(sim.name).toLowerCase()));
      });

      this.csvData.forEach((row, index) => {
        if (!row.Simulator || row.Simulator.length <= 0 || row.Simulator === '') {
          errors.push({
            row: index,
            error: 'Match doesn\'t have a simulator',
          });
        }
        if (!allSims.includes(String(row.Simulator).toLowerCase())) {
          errors.push({
            row: index,
            error: 'Simulator does not exist',
          });
        }
      });

      return errors;
    },
    // All times are valid 24h
    allTimesValid() {
      // TODO
      const errors = [];

      this.csvData.forEach((row, index) => {
        if (!row.TIME || row.TIME.length <= 0) {
          errors.push({
            row: index,
            error: '\'TIME\' field is missing',
          });
        } else {
          const timeSplit = row.TIME.split(':');
          if (parseInt(timeSplit[0], 10) > 24
              || parseInt(timeSplit[0], 10) <= 0
              || parseInt(timeSplit[1], 10) > 59
              || parseInt(timeSplit[1], 10) < 0) {
            errors.push({
              row: index,
              error: '\'TIME\' field contains an invalid date',
            });
          }
        }
      });

      return errors;
    },
    // All teams match with league
    allTeamsMatchLeague() {
      const errors = [];
      const leagueTeamNames = [];

      const leagueTeams = this.league
                          && this.league.teams
                          && this.league.teams.length > 0 ? this.league.teams : [];

      leagueTeams.forEach((team) => {
        leagueTeamNames.push(String(team.Name).toLowerCase());
      });

      this.csvData.forEach((row, index) => {
        if (!leagueTeamNames.includes(String(row['Team 1']).toLowerCase())) {
          errors.push({
            row: index,
            error: `Team 1 (${row['Team 1']}) does not exist in league`,
          });
        }
        if (this.league.type === 'team_live' && !leagueTeamNames.includes(String(row['Team 2']).toLowerCase())) {
          errors.push({
            row: index,
            error: `Team 2 (${row['Team 2']}) does not exist in league`,
          });
        }
      });

      return errors;
    },
    // Impossible matches (two matches on same sim at same time on the same week)
    noImpossibleMatches() {
      const errors = [];
      const leagueWeeks = this.league ? this.league.weeks : [];

      leagueWeeks.forEach((week) => {
        const newMatchesForWeek = this.csvData.filter(a => (
          parseInt(a.Week, 10) === parseInt(week.WeekNumber, 10)
        ));

        const existingMatchesForWeek = this.matches.filter(a => (
          a.week.WeekNumber === week.WeekNumber
        ));

        const simTimeStrings = [];

        newMatchesForWeek.forEach((newMatch) => {
          simTimeStrings.push(
            `${String(newMatch.Simulator).toLowerCase()}!${String(newMatch.TIME).toLowerCase()}`,
          );
        });
        existingMatchesForWeek.forEach((oldMatch) => {
          const hours = new Date(oldMatch.matchDateTime).getHours();
          const mins = new Date(oldMatch.matchDateTime).getHours();
          if (oldMatch.simulator) {
            simTimeStrings.push(`${String(oldMatch.simulator.name).toLowerCase()}!${hours}:${mins}`);
          }
        });

        const duplicates = [...new Set(
          simTimeStrings.filter((value, index, self) => self.indexOf(value) !== index),
        )];

        duplicates.forEach((dup) => {
          const splitDup = dup.split('!');
          errors.push({
            error: `Scheduling conflict in '${splitDup[0].toUpperCase()}' at ${splitDup[1]}`,
            warnOnly: this.league.type === 'mixed',
          });
        });
      });

      return errors;
    },
    // Teams not scheduled twice in same week
    noTeamsScheduledTwice() {
      const errors = [];
      const leagueWeeks = this.league ? this.league.weeks : [];

      leagueWeeks.forEach((week) => {
        const newMatchesForWeek = this.csvData.filter(a => (
          parseInt(a.Week, 10) === parseInt(week.WeekNumber, 10)
        ));

        const existingMatchesForWeek = this.matches.filter(a => (
          a.week.WeekNumber === week.WeekNumber
        ));

        const scheduledTeamNames = [];

        newMatchesForWeek.forEach((newMatch) => {
          scheduledTeamNames.push(String(newMatch['Team 1']).toLowerCase());
          if (this.league.type === 'team_live') {
            scheduledTeamNames.push(String(newMatch['Team 2']).toLowerCase());
          }
        });
        existingMatchesForWeek.forEach((oldMatch) => {
          oldMatch.teams.forEach((team) => {
            scheduledTeamNames.push(String(team.Name).toLowerCase());
          });
        });

        const duplicates = [...new Set(
          scheduledTeamNames.filter((value, index, self) => self.indexOf(value) !== index),
        )];

        duplicates.forEach((dup) => {
          errors.push({
            error: `${dup.toUpperCase()} is scheduled twice during Week ${week.WeekNumber}`,
          });
        });
      });

      return errors;
    },
    // Every team is scheduled every week
    allTeamsScheduled() {
      const errors = [];
      const leagueWeeks = this.league ? this.league.weeks : [];
      const leagueTeams = this.league ? this.league.teams : [];

      leagueWeeks.forEach((week) => {
        const newMatchesForWeek = this.csvData.filter(a => a.Week === week.WeekNumber);
        const existingMatchesForWeek = this.matches.filter(a => a.week.WeekNumber);

        leagueTeams.forEach((team) => {
          let found = false;

          newMatchesForWeek.forEach((newMatch) => {
            if (String(newMatch['Team 1']).toLowerCase() === String(team.Name).toLowerCase()
                || String(newMatch['Team 2']).toLowerCase() === String(team.Name).toLowerCase()) {
              found = true;
            }
          });
          existingMatchesForWeek.forEach((oldMatch) => {
            oldMatch.teams.forEach((oldMatchTeam) => {
              if (String(oldMatchTeam.Name).toLowerCase() === String(team.Name).toLowerCase()) {
                found = true;
              }
            });
          });

          if (!found) {
            errors.push({
              error: `${team.Name} is not scheduled during Week ${week.WeekNumber}`,
              warnOnly: true,
            });
          }
        });
      });

      return errors;
    },
    allErrors() {
      const allErrors = [
        ...this.areFieldsValid,
        ...this.allSimulatorsExist,
        ...this.allTeamsMatchLeague,
        ...this.allTeamsScheduled,
        ...this.allTimesValid,
        ...this.allWeeksExist,
        ...this.matchesHaveTwoTeams,
        ...this.noImpossibleMatches,
        ...this.noTeamsScheduledTwice,
      ];

      return allErrors;
    },
    canUpload() {
      const fatalErrors = this.allErrors.filter(a => (
        a.warnOnly === undefined || a.warnOnly === false
      ));
      console.log(fatalErrors.length <= 0);
      return fatalErrors.length <= 0;
    },
  },
  methods: {
    onClose() {
      this.$refs.file.value = '';
      this.csvData = [];
      this.csvErrors = [];
      this.csvFields = [];
      this.$emit('close');
    },
    uploadSchedule() {
      if (!this.canUpload) {
        return;
      }
      const matchCreationJobs = [];
      this.loading = true;
      this.csvData.forEach((match) => {
        const matchWeek = this.findWeekObject(match.Week);
        const matchTimeSplit = match.TIME.split(':');
        const matchTime = Moment(matchWeek.Start)
          .minute(parseInt(matchTimeSplit[1], 10))
          .hour(parseInt(matchTimeSplit[0], 10));
        const newMatch = {
          teams: [
            this.findTeamObject(match['Team 1']),
            this.findTeamObject(match['Team 2']),
          ],
          week: matchWeek.id,
          matchDateTime: matchTime.format(),
          simulator: this.findSimulatorObject(match.Simulator),
        };

        matchCreationJobs.push(this.createNewMatch(newMatch));

        Promise.all(matchCreationJobs)
          .then((results) => {
            this.loading = false;
            this.onClose();
            this.$notify({
              verticalAlign: 'top',
              horizontalAlign: 'center',
              message: `Success! ${results.length} matches have been imported.`,
              type: 'success',
            });
          })
          .catch((error) => {
            console.log(error);
            this.$notify({
              verticalAlign: 'top',
              horizontalAlign: 'center',
              message: 'There was an error importing the matches.',
              type: 'danger',
            });
          });
      });
    },
    createNewMatch(newMatch) {
      return this.axios
        .post('/matches', newMatch, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        });
    },
    findTeamObject(teamName) {
      const leagueTeams = this.league ? this.league.teams : [];
      let foundTeam = null;
      leagueTeams.forEach((team) => {
        if (String(teamName).toLowerCase() === String(team.Name).toLowerCase()) {
          foundTeam = team;
        }
      });
      return foundTeam;
    },
    findWeekObject(weekNumber) {
      const leagueWeeks = this.league ? this.league.weeks : [];
      let foundWeek = null;
      leagueWeeks.forEach((week) => {
        if (weekNumber === week.WeekNumber) {
          foundWeek = week;
        }
      });
      return foundWeek;
    },
    findSimulatorObject(simName) {
      const sims = this.simulators ? this.simulators : [];
      let foundSim = null;
      sims.forEach((sim) => {
        if (String(sim.name).toLowerCase() === String(simName).toLowerCase()) {
          foundSim = sim;
        }
      });
      return foundSim;
    },
    handleFileUpload() {
      // eslint-disable-next-line prefer-destructuring
      this.csvFile = this.$refs.file.files[0];
      this.loading = true;
      return new Promise((resolve) => {
        PapaParse.parse(this.csvFile, {
          worker: true,
          header: true,
          dynamicTyping: true,
          skipEmptyLines: true,
          complete(results) {
            resolve(results);
          },
        });
      })
        .then((result) => {
          console.log(result);
          this.csvData = result.data;
          this.csvErrors = result.errors;
          this.csvFields = result.meta.fields;
          this.loading = false;
        });
    },
    getAllSimulators() {
      return this.axios
        .get('/simulators', {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then((response) => {
          this.simulators = response.data;
        })
        .catch((error) => {
          ErrorHandler.handleError(error);
        });
    },
    getAllLeagueMatches() {
      const leagueWeeks = this.league ? this.league.weeks : [];

      const fetchJobs = [];
      leagueWeeks.forEach((week) => {
        fetchJobs.push(this.getWeekMatches(week.id));
      });

      Promise.all(fetchJobs)
        .then((responses) => {
          this.matches = [].concat(...responses);
        });
    },
    getWeekMatches(weekId) {
      return this.axios
        .get(`/matches?week_eq=${weekId}`, {
          headers: {
            Authorization: `Bearer ${this.$store.state.authToken}`,
          },
        })
        .then(response => response.data);
    },
  },
};
</script>
<style lang="scss" scoped>
.error-box {
  overflow-x: auto;
  border: 1px solid #f5f5f5;
  padding: .5rem;
  max-height: 200px;
  color: red;

  .error {
    font-size: .9rem;
    &:nth-child(2n){
      background: #f5f5f5;
    }
    &.warn {
      color: orange;
    }
  }
}
</style>
